import * as React from "react";

/**
 * Context that provides a shared transition state across the application.
 *
 * This allows multiple components to coordinate loading states when navigating or updating search params that trigger
 * Suspense boundaries.
 */
const SharedTransitionContext = React.createContext<[boolean, React.TransitionStartFunction] | null>(null);

/**
 * Provider that creates a shared transition context.
 *
 * Wrap your app or feature area with this provider to enable coordinated transition states across multiple components.
 *
 * @example
 *   ```tsx
 *   <SharedTransitionProvider>
 *     <YourApp />
 *   </SharedTransitionProvider>
 *   ```;
 */
export function SharedTransitionProvider({ children }: { children: React.ReactNode }) {
  const transition = React.useTransition();

  return <SharedTransitionContext.Provider value={transition}>{children}</SharedTransitionContext.Provider>;
}

/**
 * Hook that returns the shared transition state and start function.
 *
 * Use this to either:
 *
 * 1. Read the isPending state to show loading UI
 * 2. Trigger transitions that should be visible to other components
 *
 * @example
 *   ```tsx
 *   // Show loading state:
 *   const [isPending] = useSharedTransition();
 *   return <div className={isPending ? "opacity-50" : ""}>...</div>
 *
 *   // Trigger a transition:
 *   const [, startTransition] = useSharedTransition();
 *   startTransition(() => {
 *     // Update state that triggers Suspense
 *   });
 *   ```;
 */
export function useSharedTransition() {
  const context = React.useContext(SharedTransitionContext);
  if (!context) {
    throw new Error("useSharedTransition must be used within a SharedTransitionProvider");
  }
  return context;
}
